// Interval bracket rendering utilities

import { createCurlyBracketPath, calculateBracketPositions } from './curlyBrackets';
import { getConfigValue } from './config';

/**
 * Highlight specific notes in an interval and dim others
 * @param {HTMLElement} container - SVG container element
 * @param {number} startNoteIndex - Index of the first note in the interval
 * @param {number} endNoteIndex - Index of the second note in the interval
 */
export const highlightInterval = (container, startNoteIndex, endNoteIndex) => {
  const svg = container.querySelector('svg');
  if (!svg) return;
  
  const noteElements = svg.querySelectorAll('.vf-stavenote');
  
  noteElements.forEach((noteElement, index) => {
    if (index === startNoteIndex || index === endNoteIndex) {
      // Make interval notes bold/prominent
      noteElement.classList.add('interval-note-highlight');
    } else {
      // Dim other notes
      noteElement.classList.add('interval-note-dimmed');
    }
  });
};

/**
 * Clear interval highlighting from all notes
 * @param {HTMLElement} container - SVG container element
 */
export const clearIntervalHighlight = (container) => {
  const svg = container.querySelector('svg');
  if (!svg) return;
  
  const noteElements = svg.querySelectorAll('.vf-stavenote');
  noteElements.forEach(noteElement => {
    noteElement.classList.remove('interval-note-highlight', 'interval-note-dimmed');
  });
};

/**
 * Renders interval analysis brackets below a musical staff
 * @param {HTMLElement} container - SVG container element
 * @param {Array} notes - VexFlow note objects
 * @param {Array} scale - Scale interval array
 * @param {Object} stave - VexFlow stave object
 * @param {Object} options - Rendering options
 */
export const renderIntervalBrackets = (container, notes, scale, stave, options = {}) => {
  const {
    matchingPattern = null,
    bracketOffset = getConfigValue('visual.intervals.bracketMargins.bottomOffset', 30),
    textOffset = getConfigValue('visual.intervals.bracketMargins.topOffset', 24),
    color = getConfigValue('visual.intervals.color', '#a5998c'),
    fontSize = '10',
    fontFamily = 'Arial, sans-serif'
  } = options;

  // Calculate positions relative to the stave position
  const staveBottomY = stave.getY() + stave.getHeight();
  const bracketY = staveBottomY + bracketOffset;
  const textY = staveBottomY + textOffset;
  
  // Create group for all brackets
  const svgElement = container.querySelector('svg');
  const bracketsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  bracketsGroup.setAttribute('class', 'interval-brackets');
  svgElement.appendChild(bracketsGroup);
  
  // Calculate all bracket positions
  const bracketPositions = calculateBracketPositions(notes);
  
  // Create brackets and labels
  bracketPositions.forEach(({ startX, endX, centerX, intervalValue }) => {
    // Create bracket path
    const pathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const pathData = createCurlyBracketPath(startX, bracketY, endX, bracketY);
    
    pathElement.setAttribute('d', pathData);
    pathElement.setAttribute('stroke', color);
    pathElement.setAttribute('fill', 'none');
    pathElement.setAttribute('stroke-width', '1');
    pathElement.setAttribute('stroke-linecap', 'round');
    pathElement.setAttribute('stroke-linejoin', 'round');
    pathElement.setAttribute('class', 'interval-bracket');
    bracketsGroup.appendChild(pathElement);
    
    // Create interval number text
    const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    textElement.setAttribute('x', centerX);
    textElement.setAttribute('y', textY);
    textElement.setAttribute('text-anchor', 'middle');
    textElement.setAttribute('dominant-baseline', 'middle');
    textElement.setAttribute('font-family', fontFamily);
    textElement.setAttribute('font-size', fontSize);
    textElement.setAttribute('fill', color);
    textElement.setAttribute('class', 'interval-number');
    textElement.setAttribute('data-start-note', intervalValue);
    textElement.setAttribute('data-end-note', intervalValue + 1);
    textElement.textContent = scale[intervalValue].toString();
    
    // Check if this interval is part of a matching pattern
    if (matchingPattern && matchingPattern.length > 0) {
      const scaleString = scale.join('');
      const isMatching = matchingPattern.some(pattern => {
        // Find all occurrences of this pattern in the scale
        for (let i = 0; i <= scaleString.length - pattern.length; i++) {
          if (scaleString.substring(i, i + pattern.length) === pattern) {
            // Check if current interval index is within this match
            if (intervalValue >= i && intervalValue < i + pattern.length) {
              return true;
            }
          }
        }
        return false;
      });
      
      if (isMatching) {
        textElement.classList.add('interval-number-matching');
      }
    }
    
    // Add interaction handlers
    textElement.style.cursor = 'pointer';
    
    // Mouse/touch event handlers
    const handleIntervalClick = (e) => {
      // Check if playback is active - don't interfere with audio highlighting
      if (container.querySelector('.note-highlight-container')) {
        return;
      }
      
      e.stopPropagation();
      const startNoteIndex = parseInt(textElement.getAttribute('data-start-note'));
      const endNoteIndex = parseInt(textElement.getAttribute('data-end-note'));
      highlightInterval(container, startNoteIndex, endNoteIndex);
    };
    
    let hoverTimeout;
    
    const handleIntervalHover = (e) => {
      // Check if playback is active - don't interfere with audio highlighting
      if (container.querySelector('.note-highlight-container')) {
        return;
      }
      
      // Clear any pending clear timeout
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }
      
      const startNoteIndex = parseInt(textElement.getAttribute('data-start-note'));
      const endNoteIndex = parseInt(textElement.getAttribute('data-end-note'));
      highlightInterval(container, startNoteIndex, endNoteIndex);
    };
    
    const handleIntervalLeave = (e) => {
      // Use a small delay to prevent flickering from rapid mouse movements
      hoverTimeout = setTimeout(() => {
        clearIntervalHighlight(container);
      }, 50);
    };
    
    // Add event listeners
    textElement.addEventListener('click', handleIntervalClick);
    textElement.addEventListener('mouseenter', handleIntervalHover);
    textElement.addEventListener('mouseleave', handleIntervalLeave);
    
    bracketsGroup.appendChild(textElement);
  });
};